import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  AccountBalanceWalletOutlined,
  BusinessOutlined,
  ChatBubbleOutlineOutlined,
  CheckBoxOutlined,
  ConstructionOutlined,
  EngineeringOutlined,
  CreditCardOutlined,
  DescriptionOutlined,
  GavelOutlined,
  GroupOutlined,
  HomeOutlined,
  LockOutlined,
  PaidOutlined,
  PersonAddAlt1Outlined,
  ReceiptLongOutlined,
  RequestQuoteOutlined,
  SavingsOutlined,
  StorefrontOutlined,
  SwapHorizOutlined,
  UploadFileOutlined,
  VerifiedUserOutlined,
  WarningAmberOutlined,
  WaterOutlined,
} from "@mui/icons-material";

import { loanDrilldownSidebarTypes } from "../../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { usePermissionsHook } from "../../../../../hooks/usePermissionsHook";
import { useIsAcquisitions } from "../../../../../pipelines/PipelineContext";
import { docUploadModalOpen } from "../../../../../redux/actions/docUploadModalActions";
import {
  sidebarClear,
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../../redux/actions/sidebarActions";
import { partnerStorage } from "../../../../../utils/storage/partnerStorage";
import { isConstructionProduct } from "../../../tabs/LoanDrilldownLoanRelatedContactsTab/contactsSections";

/**
 * The "Add to loan" catalog — everything that can be created on a loan, and
 * the one place that decides which of it this loan / user may act on and what
 * picking it does. Read by the Add to loan panel and by the Quick Add rail
 * button's hover menu, so the two can never offer different things.
 */

// ── Categories ────────────────────────────────────────────────────────
// `short` is the filter chip, `heading` the section title. Order here is the
// order of both the chip strip and the sections.
export const CATEGORIES = [
  { key: "documents", short: "Docs", heading: "Documents" },
  { key: "people", short: "People", heading: "People and entities" },
  { key: "collateral", short: "Collateral", heading: "Collateral" },
  { key: "fees", short: "Fees", heading: "Fees and funds" },
  { key: "conditions", short: "Conditions", heading: "Conditions and terms" },
  { key: "orders", short: "Orders", heading: "Orders and reports" },
  { key: "notes", short: "Notes", heading: "Notes" },
];

// Construction products that get draws — mirrors the Budget & Draws tab gate in
// LoanDrilldownScreen. Kept local on purpose: it is a cheap client pre-filter,
// the draw endpoints remain the authority.
const BUDGET_ELIGIBLE_PRODUCTS = new Set([
  "Fix and Flip",
  "Ground Up Construction",
]);

const DOCUMENTS_KEY = "documents-upload";

// The contact form's GC role — matches `GC_ROLE` in
// DrilldownSidebarAddLoanRelatedContact (copied, not imported, so the rail's
// hover menu doesn't pull that whole form into the drilldown bundle).
const GC_ROLE = "General Contractor";

// ── The catalog ───────────────────────────────────────────────────────
// Each item: { key, category, label, description, icon, keywords?, count?,
// available?, onSelect? }.
//   keywords  — extra search terms that aren't in the label/description
//               (a user types "guarantor", not "borrower").
//   count     — ({ loan }) => how many are already on the loan (tile pill).
//   available — ({ loan, canViewLoanLock, isAcquisition }) => boolean.
//   onSelect  — custom handler; the default opens the sidebar type `key`.
export const CATALOG = [
  // Documents ---------------------------------------------------------
  {
    key: DOCUMENTS_KEY,
    category: "documents",
    label: "Document",
    description: "Loan, borrower, or property files",
    icon: UploadFileOutlined,
    keywords: "upload file attachment scan pdf statement appraisal title",
    count: ({ loan }) => loan?.loanDocuments?.length,
  },
  {
    key: loanDrilldownSidebarTypes.SIZER_UPLOAD,
    category: "documents",
    label: "Sizer",
    description: "Sizing spreadsheet",
    icon: DescriptionOutlined,
    keywords: "sizer excel xlsx import terms pricing",
  },

  // People and entities ------------------------------------------------
  {
    key: loanDrilldownSidebarTypes.ADD_BORROWER,
    category: "people",
    label: "Applicant",
    description: "Applicant, co-applicant, or guarantor",
    icon: PersonAddAlt1Outlined,
    keywords: "borrower co-borrower guarantor sponsor co-applicant person",
    count: ({ loan }) => loan?.borrowers?.length,
  },
  {
    key: loanDrilldownSidebarTypes.ADD_EDIT_ENTITY,
    category: "people",
    label: "Entity",
    description: "LLC, corporation, or trust",
    icon: BusinessOutlined,
    keywords: "llc corporation trust company vesting borrowing entity",
  },
  {
    key: loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT,
    category: "people",
    label: "Related contact",
    description: "Attorney, agent, or title officer",
    icon: GroupOutlined,
    keywords: "attorney agent title escrow officer insurance broker contact",
    count: ({ loan }) => loan?.loanRelatedContacts?.length,
  },
  // A GC is a generalContractors[] record, created through the contact form
  // opened with the GC role (the form flips to its GC questions). Construction
  // products only — the same gate the Contacts tab's GC section uses.
  {
    key: "general-contractor",
    category: "people",
    label: "General contractor",
    description: "GC licensing, insurance, and experience",
    icon: EngineeringOutlined,
    keywords: "gc general contractor builder construction license",
    count: ({ loan }) => loan?.generalContractors?.length,
    available: ({ loan }) => isConstructionProduct(loan?.loanProductType),
    onSelect: ({ dispatch }) => {
      dispatch(sidebarSetStateValue({ loanRole: GC_ROLE }));
      dispatch(
        sidebarTypeSet(loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT),
      );
    },
  },

  // Collateral ---------------------------------------------------------
  {
    key: loanDrilldownSidebarTypes.ADD_PROPERTY,
    category: "collateral",
    label: "Property",
    description: "Collateral real estate",
    icon: HomeOutlined,
    keywords: "subject property collateral address unit parcel",
    count: ({ loan }) => loan?.subjectProperties?.length,
  },

  // Fees and funds ------------------------------------------------------
  {
    key: loanDrilldownSidebarTypes.ADD_LOAN_FEE,
    category: "fees",
    label: "Loan fee",
    description: "Origination, processing, lender fees",
    icon: PaidOutlined,
    keywords: "origination underwriting processing points lender charge",
  },
  {
    key: loanDrilldownSidebarTypes.ADD_EDIT_THIRD_PARTY_FEES,
    category: "fees",
    label: "Third-party fee",
    description: "Appraisal, title, vendor charges",
    icon: ReceiptLongOutlined,
    keywords: "appraisal title vendor settlement recording survey",
  },
  {
    key: loanDrilldownSidebarTypes.ADD_EDIT_CORRESPONDENT_FEE,
    category: "fees",
    label: "Correspondent fee",
    description: "Correspondent compensation",
    icon: SwapHorizOutlined,
    keywords: "correspondent comp srp table funding",
  },
  {
    key: loanDrilldownSidebarTypes.ADD_LOAN_CREDIT,
    category: "fees",
    label: "Loan credit",
    description: "Lender or seller credit",
    icon: CreditCardOutlined,
    keywords: "credit rebate concession seller lender",
  },
  {
    key: loanDrilldownSidebarTypes.ADD_LOAN_ESCROW_ITEM,
    category: "fees",
    label: "Escrow reserve",
    description: "Taxes, insurance, interest",
    icon: SavingsOutlined,
    keywords: "escrow impound reserve taxes insurance interest holdback",
  },
  {
    key: loanDrilldownSidebarTypes.ADD_EDIT_LIQUIDITY_SOURCE,
    category: "fees",
    label: "Liquidity source",
    description: "Verified cash reserves",
    icon: AccountBalanceWalletOutlined,
    keywords: "liquidity reserves cash bank account assets verification",
  },
  // Construction products only, matching the Budget & Draws tab gate.
  {
    key: loanDrilldownSidebarTypes.ADD_DRAW,
    category: "fees",
    label: "Draw request",
    description: "Construction holdback draw",
    icon: ConstructionOutlined,
    keywords: "draw holdback rehab construction budget inspection disbursement",
    available: ({ loan }) =>
      BUDGET_ELIGIBLE_PRODUCTS.has(loan?.loanProductType),
  },

  // Conditions and terms -------------------------------------------------
  {
    key: loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION_REDESIGN,
    category: "conditions",
    label: "Condition",
    description: "Requirement to clear before closing",
    icon: CheckBoxOutlined,
    keywords: "condition stip stipulation prior to funding ptd ptf clear",
  },
  {
    key: loanDrilldownSidebarTypes.ADD_EXCEPTION,
    category: "conditions",
    label: "Exception",
    description: "Guideline exception request",
    icon: WarningAmberOutlined,
    keywords: "exception guideline waiver variance compensating factor",
  },
  {
    key: loanDrilldownSidebarTypes.ADD_EDIT_BROKER_TERMS,
    category: "conditions",
    label: "Broker terms",
    description: "Points and compensation",
    icon: RequestQuoteOutlined,
    keywords: "broker points compensation ybp yield spread",
  },
  // Same gate as the Loan Lock tab (canViewLoanLock), and on the
  // acquisitions pipeline DSCR only.
  {
    key: loanDrilldownSidebarTypes.ADD_EDIT_LOAN_LOCK,
    category: "conditions",
    label: "Rate lock",
    description: "Request or confirm the lock",
    icon: LockOutlined,
    keywords: "lock rate pricing expiration extension buy rate",
    available: ({ loan, canViewLoanLock, isAcquisition }) =>
      canViewLoanLock &&
      (!isAcquisition || loan?.loanProductType === "DSCR"),
  },

  // Orders and reports ---------------------------------------------------
  {
    key: loanDrilldownSidebarTypes.ORDER_INTERNAL_APPRAISAL,
    category: "orders",
    label: "Appraisal review",
    description: "Internal review order",
    icon: GavelOutlined,
    keywords: "appraisal review order valuation bpo cda internal",
    available: ({ loan }) => !!loan?.subjectProperties?.length,
  },
  {
    key: loanDrilldownSidebarTypes.ORDER_OUTSIDE_VALUATION,
    category: "orders",
    label: "Outside valuation",
    description: "Secondary ordered through a vendor or AMC",
    icon: StorefrontOutlined,
    keywords:
      "appraisal secondary valuation outside external third party vendor amc order",
    available: ({ loan }) => !!loan?.subjectProperties?.length,
  },
  {
    key: loanDrilldownSidebarTypes.ORDER_FLOOD_CERT,
    category: "orders",
    label: "Flood certificate",
    description: "Flood determination request",
    icon: WaterOutlined,
    keywords: "flood cert determination fema zone insurance",
    // Needs one property. One on the loan → go straight in; more than one →
    // the shared entity picker, handed the sidebar type to open after the pick.
    available: ({ loan }) => !!loan?.subjectProperties?.length,
    onSelect: ({ dispatch, loan }) => {
      const properties = loan?.subjectProperties || [];
      if (properties.length === 1) {
        dispatch(sidebarSetStateValue(properties[0]?._id));
        dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ORDER_FLOOD_CERT));
        return;
      }
      dispatch(
        sidebarSetStateValue({
          kind: "property",
          then: loanDrilldownSidebarTypes.ORDER_FLOOD_CERT,
          title: "Order Flood Certificate",
          subtitle: "Select the property to order the flood determination for.",
        }),
      );
      dispatch(
        sidebarTypeSet(loanDrilldownSidebarTypes.DOCUMENT_UPLOAD_PICKER),
      );
    },
  },
  {
    key: loanDrilldownSidebarTypes.ORDER_FUNDING_SHIELD,
    category: "orders",
    label: "Funding Shield",
    description: "Wire and title verification",
    icon: VerifiedUserOutlined,
    keywords: "funding shield wire fraud title verification closing protection",
  },

  // Notes ----------------------------------------------------------------
  {
    key: loanDrilldownSidebarTypes.COMMENTS,
    category: "notes",
    label: "Comment",
    description: "Post to the loan thread",
    icon: ChatBubbleOutlineOutlined,
    keywords: "comment note message mention thread discussion",
  },
];

/** Always first in the panel's Suggested row, ahead of the user's recents. */
const PINNED_SUGGESTED_KEYS = [
  loanDrilldownSidebarTypes.ADD_BORROWER,
  loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT,
];

/**
 * The everyday adds, in order — what the panel's Suggested row falls back to
 * once the pinned items and the user's own recents run out.
 */
const COMMON_KEYS = [
  loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION_REDESIGN,
  DOCUMENTS_KEY,
  loanDrilldownSidebarTypes.COMMENTS,
  loanDrilldownSidebarTypes.ADD_BORROWER,
  loanDrilldownSidebarTypes.ADD_PROPERTY,
  loanDrilldownSidebarTypes.ADD_EDIT_LOAN_LOCK,
];

/**
 * The Quick Add hover menu — a FIXED list, not recents, so each shortcut stays
 * where the hand expects it. `action` is the menu's verb phrasing; `hint` an
 * optional second line.
 */
export const MENU_ITEMS = [
  { key: DOCUMENTS_KEY, action: "Upload document", hint: "Or drop a file here" },
  { key: loanDrilldownSidebarTypes.ADD_BORROWER, action: "Add applicant" },
  { key: loanDrilldownSidebarTypes.ADD_PROPERTY, action: "Add property" },
  {
    key: loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION_REDESIGN,
    action: "Add condition",
  },
  { key: loanDrilldownSidebarTypes.ADD_LOAN_FEE, action: "Add loan fee" },
  { key: loanDrilldownSidebarTypes.COMMENTS, action: "Post comment" },
];

// ── Recently used ─────────────────────────────────────────────────────
// Item keys, most recent first, partner-namespaced so two partner builds on
// localhost:3000 don't share the list.
const RECENTS_KEY = "addToLoanRecents";
const RECENTS_MAX = 3;

const readRecents = () => {
  try {
    const raw = partnerStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeRecents = (keys) => {
  try {
    partnerStorage.setItem(RECENTS_KEY, JSON.stringify(keys));
  } catch {
    /* storage unavailable (private window) — recents are a convenience only */
  }
};

// ── Search ────────────────────────────────────────────────────────────

export const matchesSearch = (item, term) => {
  if (!term) return true;
  const heading = CATEGORIES.find((c) => c.key === item.category)?.heading;
  const haystack =
    `${item.label} ${item.description} ${item.keywords || ""} ${heading || ""}`.toLowerCase();
  // Every whitespace-separated token must appear, so "third fee" still finds
  // "Third-party fee" while "flood escrow" finds nothing.
  return term
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token));
};

// ── The hook ──────────────────────────────────────────────────────────

/**
 * `available` — the catalog filtered to what this loan / user can act on.
 * `select(item)` — do what picking it means (and remember it as recent).
 * `suggested(n)` — the user's recents, topped up from COMMON_KEYS, n long.
 * `countOf(item)` — how many of it are already on the loan (0 = none).
 */
export const useAddToLoanItems = () => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const { canViewLoanLock } = usePermissionsHook();
  const isAcquisition = useIsAcquisitions();
  const [recentKeys, setRecentKeys] = useState(readRecents);

  const available = useMemo(() => {
    const ctx = { loan, canViewLoanLock, isAcquisition };
    return CATALOG.filter((item) => !item.available || item.available(ctx));
  }, [loan, canViewLoanLock, isAcquisition]);

  const select = useCallback(
    (item) => {
      const nextRecents = [
        item.key,
        ...recentKeys.filter((k) => k !== item.key),
      ].slice(0, RECENTS_MAX);
      setRecentKeys(nextRecents);
      writeRecents(nextRecents);

      if (item.onSelect) {
        item.onSelect({ dispatch, loan });
        return;
      }
      if (item.key === DOCUMENTS_KEY) {
        // Straight into the reusable uploader with no target locked — its own
        // queue carries the Assign-to picker, so nobody has to say who the
        // files belong to first.
        dispatch(docUploadModalOpen(null));
        dispatch(sidebarClear());
        return;
      }
      dispatch(sidebarSetStateValue(null));
      dispatch(sidebarTypeSet(item.key));
    },
    [dispatch, loan, recentKeys],
  );

  const suggested = useCallback(
    (n) => {
      const keys = [
        ...new Set([...PINNED_SUGGESTED_KEYS, ...recentKeys, ...COMMON_KEYS]),
      ];
      return keys
        .map((key) => available.find((item) => item.key === key))
        .filter(Boolean)
        .slice(0, n);
    },
    [recentKeys, available],
  );

  const countOf = useCallback((item) => item.count?.({ loan }) || 0, [loan]);

  return { loan, available, select, suggested, countOf };
};
